import { config } from '@/config'

/**
 * Đăng nhập bằng tài khoản AloUbot: OIDC authorization code + PKCE S256, client công khai
 * `meetubot`. Đã có phiên ở AloUbot (vừa dùng chat) thì quay về ngay, không phải gõ lại mật khẩu.
 *
 * Token OIDC (mờ) chỉ dùng để xin token MeetUbot ở AloUbot (`POST /api/v1/meet/token`); máy chủ
 * MeetUbot chỉ nhận token MeetUbot (JWT `aud=meetubot`, sống 5 phút).
 */

interface Tokens {
  access: string
  refresh: string | null
  /** ms epoch */
  expiresAt: number
}

interface PendingLogin {
  state: string
  verifier: string
  returnTo: string
  at: number
}

const storage = {
  get<T>(key: string): T | null {
    try {
      const raw = localStorage.getItem(key)
      return raw ? (JSON.parse(raw) as T) : null
    } catch {
      return null
    }
  },
  set(key: string, value: unknown) {
    try {
      if (value === null) localStorage.removeItem(key)
      else localStorage.setItem(key, JSON.stringify(value))
    } catch {
      // Trình duyệt chặn lưu trữ: phiên chỉ sống tới khi đóng tab.
    }
  },
}

let memoryTokens: Tokens | null = storage.get<Tokens>(config.storageKeys.auth)

function base64url(bytes: Uint8Array): string {
  let text = ''
  for (const byte of bytes) text += String.fromCharCode(byte)
  return btoa(text).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

const randomString = (bytes: number) => base64url(crypto.getRandomValues(new Uint8Array(bytes)))
const redirectUri = () => `${location.origin}/auth/callback`

export class SessionExpired extends Error {
  constructor() {
    super('Phiên đăng nhập đã hết')
  }
}

/** Đi tới trang đăng nhập AloUbot; quay lại đúng trang đang mở. */
export async function beginLogin(returnTo = location.pathname + location.search): Promise<never> {
  const verifier = randomString(48)
  const state = randomString(24)
  storage.set(config.storageKeys.login, { state, verifier, returnTo, at: Date.now() } satisfies PendingLogin)
  const challenge = base64url(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))))
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: config.clientId,
    redirect_uri: redirectUri(),
    scope: 'openid profile email',
    state,
    code_challenge: challenge,
    code_challenge_method: 'S256',
  })
  location.assign(`${config.issuer}/auth?${params}`)
  return new Promise<never>(() => undefined)
}

async function tokenRequest(body: Record<string, string>): Promise<Tokens> {
  const res = await fetch(`${config.issuer}/token`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({ client_id: config.clientId, ...body }),
  })
  if (!res.ok) throw new SessionExpired()
  const data = (await res.json()) as { access_token: string; refresh_token?: string; expires_in: number }
  return { access: data.access_token, refresh: data.refresh_token ?? null, expiresAt: Date.now() + data.expires_in * 1000 }
}

function store(tokens: Tokens | null) {
  memoryTokens = tokens
  storage.set(config.storageKeys.auth, tokens)
}

/** Trang /auth/callback: đổi mã lấy token, trả về đường dẫn cần quay lại. */
export async function completeLogin(search: string): Promise<string> {
  const params = new URLSearchParams(search)
  const pending = storage.get<PendingLogin>(config.storageKeys.login)
  storage.set(config.storageKeys.login, null)
  if (params.get('error')) throw new Error(params.get('error_description') || 'Đăng nhập bị từ chối')
  const code = params.get('code')
  if (!pending || !code || params.get('state') !== pending.state || Date.now() - pending.at > 15 * 60_000) {
    throw new Error('Lượt đăng nhập không hợp lệ hoặc đã quá hạn, vui lòng thử lại.')
  }
  store(await tokenRequest({ grant_type: 'authorization_code', code, redirect_uri: redirectUri(), code_verifier: pending.verifier }))
  return pending.returnTo || '/'
}

export const isSignedIn = () => memoryTokens !== null

let refreshing: Promise<Tokens> | null = null

/** Token OIDC còn hạn; sắp hết thì làm mới bằng refresh token. */
async function accessToken(): Promise<string> {
  const tokens = memoryTokens ?? storage.get<Tokens>(config.storageKeys.auth)
  if (!tokens) throw new SessionExpired()
  if (tokens.expiresAt - Date.now() > 30_000) return tokens.access
  if (!tokens.refresh) throw new SessionExpired()
  refreshing ??= tokenRequest({ grant_type: 'refresh_token', refresh_token: tokens.refresh })
    .then((fresh) => {
      store(fresh)
      return fresh
    })
    .catch((error) => {
      store(null)
      throw error
    })
    .finally(() => {
      refreshing = null
    })
  return (await refreshing).access
}

export function signOut() {
  store(null)
  location.assign('/')
}

export function currentOrg(): string | null {
  return storage.get<string>(config.storageKeys.org)
}

export function setOrg(orgId: string | null) {
  storage.set(config.storageKeys.org, orgId)
}

export interface MeetSession {
  token: string
  expiresAt: number
  orgId: string
  userId: string
  name: string
  avatar: string | null
}

let meetSession: MeetSession | null = null
let meetPending: Promise<MeetSession> | null = null

function claimsOf(token: string): Record<string, unknown> {
  const part = token.split('.')[1] ?? ''
  const bytes = Uint8Array.from(atob(part.replace(/-/g, '+').replace(/_/g, '/')), (c) => c.charCodeAt(0))
  return JSON.parse(new TextDecoder().decode(bytes)) as Record<string, unknown>
}

/**
 * Khách ngoài (không có tài khoản AloUbot): token do máy chủ MeetUbot cấp cho MỘT phòng sau khi
 * nhập tên, sống vài giờ. Giữ trong tab (sessionStorage): đóng tab là hết.
 */
export interface GuestSession extends MeetSession {
  code: string
}

const GUEST_KEY = 'meetubot.guest'

function readGuest(): GuestSession | null {
  try {
    const raw = sessionStorage.getItem(GUEST_KEY)
    return raw ? (JSON.parse(raw) as GuestSession) : null
  } catch {
    return null
  }
}

/** Phiên khách còn hạn (của phòng `code` nếu đưa vào). */
export function guestSession(code?: string): GuestSession | null {
  const guest = readGuest()
  if (!guest || guest.expiresAt - Date.now() < 60_000) return null
  return code && guest.code !== code ? null : guest
}

export function setGuestSession(guest: GuestSession | null) {
  try {
    if (guest) sessionStorage.setItem(GUEST_KEY, JSON.stringify(guest))
    else sessionStorage.removeItem(GUEST_KEY)
  } catch {
    // Trình duyệt chặn lưu trữ: khách nhập tên lại khi tải lại trang.
  }
}

/** Đang dùng MeetUbot với tư cách khách ngoài (chưa đăng nhập). */
export const isGuest = () => !isSignedIn() && guestSession() !== null

/** Token MeetUbot (kèm tên, tổ chức). Xin lại khi còn dưới 60 giây. Khách ngoài: token khách. */
export async function meetToken(): Promise<MeetSession> {
  if (!isSignedIn()) {
    const guest = guestSession()
    if (guest) return guest
  }
  if (meetSession && meetSession.expiresAt - Date.now() > 60_000) return meetSession
  meetPending ??= (async () => {
    const headers: Record<string, string> = { authorization: `Bearer ${await accessToken()}` }
    const org = currentOrg()
    if (org) headers['x-ubot-org'] = org
    const res = await fetch(`${config.aloubotApi}/meet/token`, { method: 'POST', headers })
    if (res.status === 401) {
      store(null)
      throw new SessionExpired()
    }
    const body = (await res.json()) as { ok: boolean; token: string; expiresAt: string; orgId: string; message?: string }
    if (!res.ok || !body.ok) throw new Error(body.message || 'Không lấy được quyền vào MeetUbot.')
    const claims = claimsOf(body.token)
    meetSession = {
      token: body.token,
      expiresAt: Date.parse(body.expiresAt),
      orgId: body.orgId,
      userId: String(claims.sub),
      name: String(claims.name ?? ''),
      avatar: typeof claims.pic === 'string' ? claims.pic : null,
    }
    return meetSession
  })().finally(() => {
    meetPending = null
  })
  return meetPending
}
